import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  fetchTicketSessions,
  fetchTicketSessionContent,
  importTicketSession,
  triggerTicketAnalysis,
  TicketSession,
  SessionEvent,
} from './api';
import { mapEventsToChatMessages, ChatMessage, formatSessionDate } from './historySidebar.utils';
import { GET_TICKETS } from '../../pages/DomainDetails/Tickets/useGetTicketsQuery';
import { OPEN_STATUSES } from '../../pages/DomainDetails/Tickets/constants';

// ─── Types ───────────────────────────────────────────────────────────────────

interface MemorySessionsPanelProps {
  domainIdentifier: string;
  isOpen: boolean;
  onClose: () => void;
  /** When true, import is disabled (e.g. during in-flight request) */
  disabled?: boolean;
  /** Called after a successful import — passes the new session ID and messages to load into chat */
  onImportSession?: (sessionId: string, messages: ChatMessage[]) => void;
}

interface SessionWithContent extends TicketSession {
  messages?: ChatMessage[];
  loading?: boolean;
  error?: string;
  /** From the BFF-side content route: the marker's `startedAt` when the session is still processing. */
  processingStartedAt?: number;
}

/** A row in the Active or Analyzed section: the ticket's identity, joined with details from GraphQL. */
interface TicketRow {
  ticketId: string;
  /** From GraphQL if the ticket was returned; empty for Analyzed rows whose ticket is not in the current get-tickets result. */
  shortDescription: string;
  /**
   * The ticket's full `details` blob from S3 (headers + operator description + interceptor-summary
   * block). Sent along to the TICKET-channel invoke so the agent has the whole ticket context, not
   * just the id/domain/region tuple. Empty for Analyzed rows whose ticket is not in the current
   * get-tickets result.
   */
  description: string;
  /** From GraphQL; empty for Analyzed rows whose ticket is not in the current get-tickets result. */
  status: string;
  /** Present for Analyzed rows: the Memory session that holds the analysis. */
  session?: TicketSession;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function MemorySessionsPanel({ domainIdentifier, isOpen, onClose, disabled, onImportSession }: MemorySessionsPanelProps) {
  const [sessions, setSessions] = useState<SessionWithContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionPrefix, setSessionPrefix] = useState('');
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  /** The ticket a request is currently in flight for. One at a time — the row is disabled while set. */
  const [analyzingTicketId, setAnalyzingTicketId] = useState<string | null>(null);
  const [triggerError, setTriggerError] = useState<string | null>(null);

  /**
   * The Overview tab's Tickets table hits this exact query; Apollo's cache is shared, so opening the
   * panel over an already-loaded Tickets tab is free. `skip: !isOpen` avoids the fetch when the panel
   * is closed (React still mounts the root because the parent decides visibility).
   */
  const {
    data: ticketsData,
    loading: ticketsLoading,
    error: ticketsError,
    refetch: refetchTickets,
  } = useQuery(GET_TICKETS, {
    variables: { domainId: domainIdentifier },
    skip: !isOpen,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  /**
   * The two lists the panel shows, derived once per data change:
   *   • Analyzed = every ticket that has a Merlin session, enriched with GraphQL description/status. A
   *     session whose ticket is no longer returned by GraphQL still renders — it happened, and the
   *     history is what matters.
   *   • Active   = OPEN GraphQL tickets not already in Analyzed. `OPEN_STATUSES` (Assigned / Pending /
   *     Researching / Work In Progress) is the same list the Overview tab uses, so the two panels
   *     cannot disagree. Closed tickets that never had an analysis don't show anywhere.
   *
   * The de-dupe constraint ("if a ticket is in Analyzed, don't put it in Active") is enforced by the
   * `!analyzedIds.has(...)` filter on the Active side — no ticket can appear in both sections.
   */
  const { activeTickets, analyzedTickets } = useMemo(() => {
    const rawTickets: any[] = Array.isArray(ticketsData?.getTickets)
      ? ticketsData.getTickets
      : ticketsData?.getTickets
      ? [ticketsData.getTickets]
      : [];
    const ticketById = new Map<string, { id: string; shortDescription: string; description: string; status: string }>();
    for (const r of rawTickets) {
      if (!r || typeof r !== 'object') continue;
      const id = String(r.id ?? '');
      if (!id) continue;
      ticketById.set(id, {
        id,
        shortDescription: String(r.short_description ?? ''),
        description: String(r.details ?? ''),
        status: String(r.status ?? ''),
      });
    }

    const analyzedIds = new Set(sessions.map((s) => s.ticketId));
    const analyzed: TicketRow[] = sessions.map((session) => {
      const details = ticketById.get(session.ticketId);
      return {
        ticketId: session.ticketId,
        shortDescription: details?.shortDescription ?? '',
        description: details?.description ?? '',
        status: details?.status ?? '',
        session,
      };
    });

    const active: TicketRow[] = Array.from(ticketById.values())
      .filter((t) => !analyzedIds.has(t.id) && OPEN_STATUSES.includes(t.status))
      .map((t) => ({
        ticketId: t.id,
        shortDescription: t.shortDescription,
        description: t.description,
        status: t.status,
      }));

    return { activeTickets: active, analyzedTickets: analyzed };
  }, [ticketsData, sessions]);

  const sortedAnalyzed = useMemo(() => {
    return [...analyzedTickets].sort((a, b) => {
      const t = (row: TicketRow) => {
        const s = row.session;
        if (!s) return 0;
        return typeof s.updatedAt === 'number'
          ? s.updatedAt
          : typeof s.createdAt === 'number'
          ? s.createdAt
          : new Date(s.updatedAt || s.createdAt).getTime() / 1000;
      };
      return t(b) - t(a);
    });
  }, [analyzedTickets]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Fetch sessions when panel opens
  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchTicketSessions(domainIdentifier);
      setSessions(result.sessions);
      setSessionPrefix(result.sessionPrefix);
    } catch (err: any) {
      setError(err.message || 'Failed to load ticket sessions');
    } finally {
      setLoading(false);
    }
  }, [domainIdentifier]);

  useEffect(() => {
    if (isOpen) {
      loadSessions();
    }
  }, [isOpen, loadSessions]);

  /**
   * Start an analysis for an Active ticket. The BFF writes the processing marker first (so the ticket
   * appears in the sessions list — and therefore in Analyzed — for every viewer at the same instant),
   * then invokes AgentCore. When the response lands we refresh the sessions so the analyzed content
   * takes the row's place. If the trigger fails, `triggerError` is surfaced and the ticket stays where
   * the current session data puts it — the marker write may or may not have landed, so we always
   * refresh regardless of outcome.
   */
  const handleAnalyze = useCallback(async (ticketId: string, description: string) => {
    if (analyzingTicketId) return; // one in flight at a time
    setAnalyzingTicketId(ticketId);
    setTriggerError(null);
    try {
      await triggerTicketAnalysis(domainIdentifier, ticketId, description);
    } catch (err: any) {
      setTriggerError(err.message || 'Failed to trigger ticket analysis');
    } finally {
      setAnalyzingTicketId(null);
      loadSessions();
    }
  }, [analyzingTicketId, domainIdentifier, loadSessions]);

  /** Read all pages of a session's content and return the mapped messages + the marker's startedAt. */
  const loadSessionContent = useCallback(async (sessionId: string) => {
    let allEvents: SessionEvent[] = [];
    let token: string | undefined;
    let processingStartedAt: number | undefined;
    do {
      const result = await fetchTicketSessionContent(sessionId, token);
      allEvents = allEvents.concat(result.events);
      token = result.nextToken;
      if (result.processingStartedAt) processingStartedAt = result.processingStartedAt;
    } while (token);
    return {
      messages: mapEventsToChatMessages(allEvents, sessionId),
      processingStartedAt,
    };
  }, []);

  // Handle selecting a session — loads its content preview
  const handleSelectSession = useCallback(async (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setImportSuccess(null);

    // Only skip the fetch when we already have real analysis content. A previously-loaded marker-only
    // session must re-fetch so the poll can pick up new content.
    const existing = sessions.find(s => s.sessionId === sessionId);
    if (existing?.messages && existing.messages.length > 0) return;

    setSessions(prev => prev.map(s =>
      s.sessionId === sessionId ? { ...s, loading: true, error: undefined } : s
    ));

    try {
      const { messages, processingStartedAt } = await loadSessionContent(sessionId);
      setSessions(prev => prev.map(s =>
        s.sessionId === sessionId ? { ...s, messages, processingStartedAt, loading: false } : s
      ));
    } catch (err: any) {
      setSessions(prev => prev.map(s =>
        s.sessionId === sessionId ? { ...s, error: err.message, loading: false } : s
      ));
    }
  }, [sessions, loadSessionContent]);

  /**
   * While the selected session is still processing (marker present, no messages yet), re-read its
   * content every 5 s. Guarded on the current session's state, so the interval is skipped when the
   * session is already analyzed or when nothing is selected. Cleans up on selection change / unmount.
   */
  useEffect(() => {
    if (!selectedSessionId) return;
    const target = sessions.find(s => s.sessionId === selectedSessionId);
    const isProcessing = !!target?.processingStartedAt && (target.messages?.length ?? 0) === 0;
    if (!isProcessing) return;
    const sessionId = selectedSessionId;
    let cancelled = false;
    const id = setInterval(async () => {
      if (cancelled) return;
      try {
        const { messages, processingStartedAt } = await loadSessionContent(sessionId);
        if (cancelled) return;
        setSessions(prev => prev.map(s =>
          s.sessionId === sessionId ? { ...s, messages, processingStartedAt } : s
        ));
      } catch {
        /* transient — keep polling */
      }
    }, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedSessionId, sessions, loadSessionContent]);

  // Import session into user's memory and open in chat
  const handleImport = useCallback(async () => {
    if (!selectedSessionId) return;
    setImporting(true);
    setImportSuccess(null);

    try {
      const result = await importTicketSession(selectedSessionId, domainIdentifier);
      setImportSuccess(`Imported ${result.copiedEvents} events`);

      // Load the messages for the new session and pass to chat
      const selectedSession = sessions.find(s => s.sessionId === selectedSessionId);
      if (onImportSession && selectedSession?.messages) {
        onImportSession(result.newSessionId, selectedSession.messages);
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import session');
    } finally {
      setImporting(false);
    }
  }, [selectedSessionId, domainIdentifier, sessions, onImportSession, onClose]);

  if (!isOpen) return null;

  const selectedSession = sessions.find(s => s.sessionId === selectedSessionId);
  const listBusy = loading || ticketsLoading;

  /**
   * Either data source down = whole sidebar unusable, and it is more honest to say so than to render a
   * half-working sidebar. Without ticket details we cannot describe Active OR enrich Analyzed rows;
   * without Merlin sessions we cannot compute the "which tickets are already analyzed" dedupe, so
   * Active would list tickets that already have analyses. Rendering one list on its own would lie about
   * which state a ticket is in.
   */
  const fatalTumbler = ticketsError ? ticketsError.message : null;
  const fatalSessions = error;
  const fatal = fatalTumbler || fatalSessions;
  const fatalSource = fatalTumbler ? 'ticket details' : fatalSessions ? 'ticket analysis history' : null;
  const retryAll = () => {
    setError(null);
    loadSessions();
    // Apollo's `refetch` can return `undefined` when the underlying query hasn't been observed yet
    // (e.g. skipped since panel open, or during HMR); wrap so the failure path is a no-op.
    // The refetched error, if any, will land on `ticketsError` and re-render this same fatal view.
    Promise.resolve(refetchTickets?.()).catch(() => {});
  };

  /**
   * Both data sources are needed to render an honest sidebar: without Merlin sessions we cannot know
   * which tickets are already Analyzed, and without get-tickets we cannot describe the rows. While either
   * is still in flight (and neither has errored), we hold back the sections entirely and show one loading
   * state — otherwise Active would flash tickets that are actually already Analyzed until the sessions
   * arrived, and Analyzed rows would render without their description/status.
   */
  const initialLoading = !fatal && listBusy;

  return (
    <div className="rv-memory-panel" role="dialog" aria-label="Ticket Analysis Sessions">
      <div className="rv-memory-panel__header">
        <h3 className="rv-memory-panel__title">
          <span className="rv-memory-panel__icon">🎫</span>
          Ticket Analyses
        </h3>
        <button className="rv-memory-panel__close" onClick={onClose} aria-label="Close panel">
          ×
        </button>
      </div>

      {fatal ? (
        <div className="rv-memory-panel__body">
          <div className="rv-memory-panel__fatal" role="alert">
            <div className="rv-memory-panel__fatal-title">Ticket analyses are unavailable</div>
            <div className="rv-memory-panel__fatal-text">
              An internal error occurred while loading the {fatalSource}. Ticket analyses cannot be
              viewed right now.
            </div>
            <div className="rv-memory-panel__fatal-detail">{fatal}</div>
            <button
              className="rv-memory-panel__start-btn"
              onClick={retryAll}
              disabled={listBusy}
            >
              {listBusy ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        </div>
      ) : initialLoading ? (
        <div className="rv-memory-panel__body">
          <div
            className="rv-memory-panel__loading-view"
            role="status"
            aria-label="Loading ticket analyses"
          >
            <div className="rv-memory-panel__spinner" aria-hidden="true" />
          </div>
        </div>
      ) : (
      <div className="rv-memory-panel__body">
        {/* Left column: two sections — Active tickets (from GraphQL, not yet analyzed) and
            Analyzed tickets (from Merlin sessions, enriched from GraphQL). */}
        <div className="rv-memory-panel__list">
          <div className="rv-memory-panel__session-list" role="listbox" aria-label="Tickets">
            {/* ── Active tickets ── */}
            <section className="rv-memory-section">
              <div className="rv-memory-section__header">
                <span className="rv-memory-section__title">Active tickets</span>
                <span className="rv-memory-section__count">{activeTickets.length}</span>
              </div>
              {activeTickets.length === 0 && (
                <div className="rv-memory-panel__empty">No active tickets awaiting analysis.</div>
              )}
              {triggerError && (
                <div className="rv-memory-panel__error" role="alert">{triggerError}</div>
              )}
              {activeTickets.map((t) => {
                const isAnalyzing = analyzingTicketId === t.ticketId;
                return (
                  <div
                    key={t.ticketId}
                    className={`rv-memory-session-item ${isAnalyzing ? 'rv-memory-session-item--selected' : ''}`}
                    role="option"
                    aria-selected={isAnalyzing}
                  >
                    <button
                      className="rv-memory-session-item__main"
                      onClick={() => handleAnalyze(t.ticketId, t.description)}
                      disabled={analyzingTicketId !== null}
                      title={
                        analyzingTicketId
                          ? isAnalyzing
                            ? 'Analysis in progress…'
                            : 'Another analysis is in progress'
                          : 'Click to start analysis'
                      }
                    >
                      <div className="rv-memory-session-item__info">
                        <span className="rv-memory-session-item__name">🎫 {t.ticketId}</span>
                        {t.shortDescription && (
                          <span className="rv-memory-session-item__desc">{t.shortDescription}</span>
                        )}
                        <span className="rv-memory-session-item__date">
                          {isAnalyzing ? 'Analyzing…' : t.status}
                        </span>
                      </div>
                    </button>
                  </div>
                );
              })}
            </section>

            {/* ── Analyzed tickets ── */}
            <section className="rv-memory-section">
              <div className="rv-memory-section__header">
                <span className="rv-memory-section__title">Analyzed tickets</span>
                <span className="rv-memory-section__count">{analyzedTickets.length}</span>
              </div>
              {analyzedTickets.length === 0 && (
                <div className="rv-memory-panel__empty">No ticket analyses yet.</div>
              )}
              {sortedAnalyzed.map((t) => {
                const isSelected = t.session?.sessionId === selectedSessionId;
                return (
                  <div
                    key={t.session!.sessionId}
                    className={`rv-memory-session-item ${isSelected ? 'rv-memory-session-item--selected' : ''}`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <button
                      className="rv-memory-session-item__main"
                      onClick={() => handleSelectSession(t.session!.sessionId)}
                    >
                      <div className="rv-memory-session-item__info">
                        <span className="rv-memory-session-item__name">🎫 {t.ticketId}</span>
                        {t.shortDescription && (
                          <span className="rv-memory-session-item__desc">{t.shortDescription}</span>
                        )}
                        <span className="rv-memory-session-item__date">
                          {formatSessionDate(t.session!.updatedAt || t.session!.createdAt)}
                        </span>
                      </div>
                    </button>
                  </div>
                );
              })}
            </section>
          </div>
        </div>

        {/* Session Content */}
        <div className="rv-memory-panel__content">
          {!selectedSession && (
            <div className="rv-memory-panel__content-empty">
              Select an analyzed ticket to preview its content.
            </div>
          )}

          {selectedSession?.loading && (
            <div className="rv-memory-panel__content-loading">Loading analysis content…</div>
          )}

          {selectedSession?.error && (
            <div className="rv-memory-panel__content-error" role="alert">
              Failed to load content: {selectedSession.error}
            </div>
          )}

          {/* Marker-only session — analysis started but no content yet. Poll effect keeps this fresh. */}
          {selectedSession?.messages && selectedSession.messages.length === 0 && selectedSession.processingStartedAt && (
            <div className="rv-memory-panel__conversation">
              <div className="rv-memory-panel__content-header">
                <h4>Ticket {selectedSession.ticketId} — Analysis</h4>
                <span className="rv-memory-panel__msg-count">Processing…</span>
              </div>
              <div className="rv-memory-panel__thinking">
                <div className="rv-memory-panel__spinner" aria-hidden="true" />
                <span>
                  Started at{' '}
                  {new Date(selectedSession.processingStartedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  . This view refreshes when the analysis completes.
                </span>
              </div>
            </div>
          )}

          {selectedSession?.messages && selectedSession.messages.length > 0 && (
            <div className="rv-memory-panel__conversation">
              <div className="rv-memory-panel__content-header">
                <h4>Ticket {selectedSession.ticketId} — Analysis</h4>
                <span className="rv-memory-panel__msg-count">
                  {selectedSession.messages.length} messages
                </span>
              </div>
              <div className="rv-memory-panel__messages">
                {selectedSession.messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`rv-memory-msg ${msg.role === 'user' ? 'rv-memory-msg--user' : 'rv-memory-msg--assistant'}`}
                  >
                    <div className="rv-memory-msg__role">
                      {msg.role === 'user' ? '📝 Ticket Content' : '🤖 Analysis'}
                    </div>
                    <div className="rv-memory-msg__content">
                      {msg.content.length > 500
                        ? msg.content.slice(0, 500) + '…'
                        : msg.content}
                    </div>
                    <div className="rv-memory-msg__time">
                      {new Date(msg.timestamp).toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Import button */}
              <div className="rv-memory-panel__actions">
                <button
                  className="rv-memory-panel__import-btn"
                  onClick={handleImport}
                  disabled={importing || disabled}
                >
                  {importing ? 'Importing…' : disabled ? '⏳ Wait for response…' : '⬇️ Import & Continue Chat'}
                </button>
                {importSuccess && (
                  <span className="rv-memory-panel__import-success">✓ {importSuccess}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
